/**
 * @file grabcad-mixin.ts
 *
 * GrabCAD environment detection for as-app. The GrabCAD protocol flows
 * (export TX, open-file RX, load-and-convert RX) are plain module functions in
 * logic/importExport/grabcad/grabcad.ts — import them directly (see
 * docs/export.md). Only the element-bound parts live here.
 *
 * Kept separate from the core app because GrabCAD is an optional integration
 * that only activates when window.opener is the GrabCAD host.
 */
import { getEnvForOrgId, getEnvForEnvironment } from '../../../data/envRegistry.ts';
import { liveAppData } from '../../../store/store.ts';
import { type Constructor } from './mixin-types-utils.ts';
import type { MeshMixinMembers } from './mesh-mixin.ts';

/** Surface added by GrabCADMixin. */
export interface GrabCADMixinMembers {
  /** True when running inside a GrabCAD-hosted environment. */
  isInGrabCAD(): boolean;
  /** Internal env detection; called after userData is set. */
  _initGrabCADEnv(): void;
}

export const GrabCADMixin = <T extends Constructor<HTMLElement & MeshMixinMembers>>(Base: T) =>
  class GrabCADMixin extends Base {
    /** True when running inside a GrabCAD-hosted environment. */
    isInGrabCAD(): boolean {
      return liveAppData.appEnv === 'grabcad';
    }

    /** Call after userData is set. Sets env on the element based on the registered org-ID map.
     *  Skips if env is already explicitly set (URL param / HTML attribute takes priority). */
    _initGrabCADEnv(): void {
      const el = this as unknown as HTMLElement;
      if (el.hasAttribute('env')) return;

      const userData = this.userData;
      if (!userData) return;

      // 1. Check environment name (new way)
      if (userData.environment) {
        const env = getEnvForEnvironment(userData.environment);
        if (env) {
          el.setAttribute('env', env);
          return;
        }
      }

      // 2. Check organizationId (legacy way)
      const orgId = userData.organizationId;
      if (orgId !== undefined) {
        const env = getEnvForOrgId(orgId);
        if (env) el.setAttribute('env', env);
      }
    }
  };
